// eslint-disable-next-line no-unused-vars
import React from 'react';
import { func, string, bool } from 'prop-types';
import { Link } from 'react-router-dom';
import { Button, Loading } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { Package, ChevronDown, Copy, BadgePercent, Megaphone, Boxes, SquareStack } from 'lucide-react';

import ProductPropType from 'Types/product';

import Img from 'Containers/Image/Image';
import Badges from 'Components/Badges/Badges';
import QuantitySelect from 'Containers/QuantitySelector/QuantitySelect';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';
import Price from 'Components/Price/Price';
import {
  StyledProductTableItem,
  StyledProductTableItemDetails
} from 'Components/Products/StyledProductItems';
import useToastNotifications from 'Hooks/useToastNotifications';
import copyToClipboard from 'Utils/copyToClipboard';
import highlightText from 'Utils/highlightText';
import { UserContext } from 'Contexts/UserContext';
import { renderTableStock } from 'Components/Products/helpers';

const ProductTableItem = ({
  product,
  onStockAlertModalOpen,
  onChangeQuantity,
  handleOnToggle,
  open,
  loading,
  error
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const toast = useToastNotifications();
  const userData = useContextSelector(UserContext, ({ data }) => data);
  const accountManagerPhone = userData?.accountManager?.phone;
  const withVat = userData?.withVat;

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  return (
    <>
      <StyledProductTableItem
        theme={ { ...theme, isOpen: open, isLoading: loading, hasError: !!error } }
      >
        <td className="thumbnail">
          <Link
            to={ `/products/${ product.slug }/pd/${ product.id }` }
            state={ {
              component: 'product_table_item',
              source: 'product_table_page',
              clickedOn: 'image',
              productId: product.id
            } }
          >
            <Img
              className="image"
              src={ (product?.pictures || [])?.at(0)?.urlThumbnailPicture }
              unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
              loading="lazy"
              loader={ <Loading withOverlay={ false } small /> }
              alt={ product.name }
            />
          </Link>
        </td>
        <td className="code">
          <div className="container">
            <p>
              <Link
                to={ `/products/${ product.slug }/pd/${ product.id }` }
                state={ {
                  component: 'product_table_item',
                  source: 'product_table_page',
                  clickedOn: 'code',
                  productId: product.id
                } }
              >
                { product.code }
              </Link>
              <span><Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(product?.code || '') } /></span>
            </p>
          </div>
        </td>
        <td className="name">
          <Link
            to={ `/products/${ product.slug }/pd/${ product.id }` }
            state={ {
              component: 'product_table_item',
              source: 'product_table_page',
              clickedOn: 'title',
              productId: product.id
            } }
          >
            { highlightText(product.title, product?.highlighted?.title) }
          </Link>
          { (product?.supplierOfferId || product?.hasSupplierOffers) && (
            <div className="supplierInfo">
              <Badges
                isSupplierOffer={ !product?.hasPrice }
                hasSupplierOffers={ product.hasSupplierOffers }
              />
              <DeliveryEstimate
                estimate={ product?.supplierOfferDeliveryTimeDays }
                isExternalSupplier={ !product?.hasPrice }
              />
            </div>
          ) }
        </td>
        <td className="stock">
          { !product?.warnings?.blocked_stocks && renderTableStock({ t, product, info: { accountManagerPhone } }) }
        </td>
        <td className="price">
          { (!product?.hasPriceRequest && !product?.warnings?.blocked_prices) && (
            <Price
              price={ product.price }
              promoPrice={ product.promoPrice }
              withTooltip
              withVat={ withVat }
              displayVatInfo
            />
          )}
        </td>
        <td className="promotions">
          { !product?.hasPriceRequest && !product.warnings?.blocked_prices && (
            <Badges
              specialPriceValidTo={ product.specialPriceValidTo }
              specialPrice={
                product.specialPrice && !product.isResealed
                  ? (product.price !== product.promoPrice && ((product.price - product.promoPrice) / product.price) >= 0.01)
                  : false
              }
              fixedPrice={ product.fixedPrice }
              isEol={ product.isEol }
              isClearance={ product.stockClearingPrice }
            >
              { product.appliedPromotions.length > 0 && (
                <span className="badge isPromotional">
                  <Boxes />
                  { (
                    !product.isEol
                    && !product.stockClearingPrice
                    && !product.specialPriceValidTo
                    && product.price === product.promoPrice
                  )
                    ? t('products.promoPackage')
                    : null
                  }
                </span>
              ) }
              { (!product.appliedPromotions.length > 0 && !!product?.priceScales?.length) && (
                <span className="badge isPromotional">
                  <BadgePercent />
                </span>
              ) }
            </Badges>
          ) }
        </td>
        <td className="quantity">
          { (
            (product.stockValue === 0 || product.stockValue < product.minQuantity)
            && !Object.keys(product?.warnings || {}).length
            && !product.hasPriceRequest
          )
            ? (
              <Button
                className="red stockAlert"
                kind="danger--ghost"
                data-test="onStockAlert"
                data-testid={ product.productId }
                onClick={ onStockAlertModalOpen }
              >
                <Megaphone />
                { t('products.stockAlert') }
              </Button>
            )
            : (
              <>
                { product.minQuantity > 1 && (
                  <p className="minQuantity">
                    <SquareStack />
                    { t('products.multipleOf', { multiple: product.minQuantity }) }
                  </p>
                ) }
                <QuantitySelect
                  disabled={ (
                    product.warnings?.blocked_stocks
                    || product.warnings?.blocked_prices
                    || product.warnings?.blocked_orders
                    || product.hasPriceRequest
                  ) }
                  value={ product.quantity || 0 }
                  onChange={ onChangeQuantity }
                  min={ Math.max(product.minQuantity, 1) }
                  step={ Math.max(product.minQuantity, 1) }
                  max={ product.stockValue }
                  zeroAllowed
                  size="md"
                  product={ product }
                />
              </>
            )}
        </td>
        <td className="details">
          <Button
            kind="ghost"
            className="details"
            onClick={ handleOnToggle }
            data-test="onToggle"
          >
            <ChevronDown />
          </Button>
        </td>
      </StyledProductTableItem>
      <StyledProductTableItemDetails
        theme={ { ...theme, isOpen: open, isLoading: loading, hasError: !!error } }
      >
        <td colSpan={ 8 }>
          { loading && (<Loading withOverlay={ false } />) }
          { (!loading && !!error) && (<p className="error">{ error }</p>) }
          { (!loading && !error) && (
            <div className="data">
              { (product?.groupAttributes || []).map(attributeGroup => (
                <div key={ attributeGroup.group_id }>
                  <p className="title">
                    { attributeGroup.group_name || t('products.miscAttributeGroup') }
                  </p>
                  <div className="attributeData">
                    { attributeGroup.properties.map((prop, idx) => (
                      <div
                        key={ prop.name_id }
                        className={ `row ${ idx % 2 ? 'even' : 'odd' }` }
                      >
                        <p className="name">{ prop.name }</p>
                        <p className="values">{ prop.values.map(v => v.value).join(', ') }</p>
                      </div>
                    )) }
                  </div>
                </div>
              )) }
            </div>
          ) }
          { (!loading && !error) && (
            <Link
              to={ `/products/${ product.slug }/pd/${ product.id }` }
              state={ {
                component: 'product_table_item',
                source: 'product_table_page',
                clickedOn: 'go_to_product_page',
                productId: product.id
              } }
            >
              { t('products.goToProductPage') }
              <ChevronDown />
            </Link>
          ) }
        </td>
      </StyledProductTableItemDetails>
    </>
  );
};

ProductTableItem.propTypes = {
  product: ProductPropType,
  error: string,
  open: bool,
  loading: bool,
  handleOnToggle: func,
  onStockAlertModalOpen: func,
  onChangeQuantity: func
};

export default ProductTableItem;
